import { BuyerFeeLine, PaymentSessionResponse } from '@/lib/api/generated/models'

/**
 * FF-3182 (EPIC-54 STORY-08) — the QA harness renders what buyers will see.
 *
 * Mirrors the SDK iframe-app's `buyerFeeLines` resolution (FF-3181) so a tester looking at
 * `/payments` sees the same rows, under the same labels, as the production checkout. The two
 * live in separate repos and cannot share a module; keep them in step by hand.
 *
 * The session's `feeBreakdown` arrives ready to render — the server has already dropped any
 * component with no configured label, and any the merchant absorbs. Three states:
 *
 *  - `undefined` — backend predates the carrier; fall back to the flat fee fields.
 *  - `[]` — nothing the buyer bears is itemisable. Render no fee rows at all: the buyer is
 *    charged a round amount and the fees come out of it invisibly.
 *  - non-empty — render exactly these, in order, under these labels.
 */

export const PLATFORM_FEE = 'PLATFORM_FEE'
export const PARTNER_SERVICE_FEE = 'PARTNER_SERVICE_FEE'

/**
 * `legacy` means the API sent no `feeBreakdown` and these rows were synthesised.
 *
 * The synthesised labels are singular — "Service Fee" / "Platform Fee" — matching what the
 * production SDK's own flat-field fallback renders (`frontend/hpp-sdk/src/utils/buyerFeeLines.ts`)
 * and what the backend now defaults an unlabelled service fee to
 * (`FeeLineItemPresenter.SERVICE_FEE_LABEL`). They were plural until FF-3595, which left this
 * harness as the only surface still showing the plural.
 */
export type FeeLineSource = 'configured' | 'legacy'

export interface ResolvedBuyerFees {
  lines: BuyerFeeLine[]
  source: FeeLineSource
}

export function resolveBuyerFeeLines(session: PaymentSessionResponse): ResolvedBuyerFees {
  if (Array.isArray(session.feeBreakdown)) {
    return { lines: session.feeBreakdown, source: 'configured' }
  }

  const lines: BuyerFeeLine[] = []
  if (session.serviceFees > 0) {
    lines.push({
      componentType: PARTNER_SERVICE_FEE,
      label: 'Service Fee',
      amountCents: session.serviceFees,
    })
  }
  if (session.platformFees > 0) {
    lines.push({
      componentType: PLATFORM_FEE,
      label: 'Platform Fee',
      amountCents: session.platformFees,
    })
  }

  return { lines, source: 'legacy' }
}

export function sumFeeLineCents(lines: BuyerFeeLine[]): number {
  return lines.reduce((total, line) => total + line.amountCents, 0)
}

export interface DerivedSubtotal {
  /** May be negative when the derivation breaches conservation — see {@link DerivedSubtotal.shortfallCents}. */
  subtotalCents: number
  /** Non-zero when the fee lines exceed the charged total — a server-side conservation bug. */
  shortfallCents: number
}

/**
 * Subtotal that makes the column add up: whatever the configuration chose not to itemise gets
 * folded in, so Subtotal + fee rows equals the charged total. With everything absorbed the fee
 * rows are empty and this is simply the total — the buyer's view of a round price.
 *
 * FF-3273 — the invariant can only fail one way, and it must not fail quietly. If the fee lines
 * exceed the charged total the column cannot be made to sum, and there is no subtotal that would
 * make it. That is a server bug: the backend's own `FeeLineItemPresenter` ends with a fail-loud
 * conservation check and throws the response rather than ship fee amounts that do not reconcile,
 * so a breach reaching a client means something upstream got past it.
 *
 * Rather than clamp to zero and carry on — which converts that bug into a quiet wrong number —
 * this returns the shortfall alongside the raw subtotal. `shortfallCents > 0` is the single
 * condition every caller keys off; none can forget to check, because the subtotal is no longer
 * reachable without seeing it.
 *
 * This function is **pure**: it decides, it does not report. Reporting is
 * {@link reportSubtotalBreach}, which the caller fires from a commit-phase effect. A
 * `console.error` in here would run during render — twice per mount under `React.StrictMode`,
 * and again on every React Query refetch — turning one server bug into a stream of identical
 * errors in the console this harness asks a tester to read.
 *
 * Mirrors `deriveDisplaySubtotal` in fanfair-web-flow (`src/utils/buyerFeeLines.ts`), which
 * carries the same rule in dollars with a tax term and a sub-cent tolerance. Keep the *rule* in
 * step by hand — the two are not line-for-line identical and cannot be diffed as text.
 */
export function deriveSubtotal(totalCents: number, lines: BuyerFeeLine[]): DerivedSubtotal {
  const subtotalCents = totalCents - sumFeeLineCents(lines)
  return { subtotalCents, shortfallCents: subtotalCents < 0 ? -subtotalCents : 0 }
}

/**
 * Reports a conservation breach found by {@link deriveSubtotal} — the total charged, every line
 * amount that went into the sum, and the shortfall, so the server-side bug can be identified
 * from the console alone.
 *
 * `console.error`, not a gated debug logger: this is a real failure and must surface in deployed
 * builds too. Callers are responsible for firing it once per distinct breach rather than once
 * per render — see the effect in `components/order-summary`.
 */
export function reportSubtotalBreach(totalCents: number, lines: BuyerFeeLine[], shortfallCents: number): void {
  console.error('[BuyerFeeLines] Fee lines exceed the charged total — the column cannot sum', {
    totalCents,
    lines: lines.map((line) => `${line.componentType}=${line.amountCents}`),
    feeTotalCents: sumFeeLineCents(lines),
    shortfallCents,
  })
}
